import { Component, OnDestroy, OnInit } from '@angular/core';
import { ColDef, GridOptions, GridReadyEvent, IDatasource, IGetRowsParams, ITextFilterParams } from 'ag-grid-community';

import { Alert } from '../../models/location.model';
import { PublicApiService } from '../../services/public-api.service';

// Make Math available to the template
declare global {
  interface Window {
    Math: typeof Math;
  }
}

@Component({
  selector: 'app-non-location-alerts',
  templateUrl: './non-location-alerts.component.html',
  styleUrls: ['./non-location-alerts.component.scss'],
})
export class NonLocationAlertsComponent implements OnInit, OnDestroy {
  private dataSource: IDatasource = {
    getRows: (params: IGetRowsParams) => {
      const startRow = params.startRow;
      const pageSize = params.endRow - params.startRow;
      const page = Math.floor(startRow / pageSize) + 1;
      const filters = JSON.stringify(params.filterModel ?? {});
      const sorters = JSON.stringify(params.sortModel ?? {});
      const request = this.publicApiService.getNonLocationAlerts(filters, sorters, page, pageSize);
      request.subscribe({
        next: (response: any) => {
          this.loading = false;
          this.numRows = response.total;
          params.successCallback(response.data, response.total);
        },
        error: (err: any) => {
          console.error('Error loading alerts:', err);
          this.loading = false;
          this.error = err.message;
          params.failCallback();
        },
      });
    },
  };

  numRows = 0;
  loading: boolean = true;
  error: string = '';
  selectedAlert: Alert | null = null;

  columnDefs: ColDef[] = [
    {
      field: 'created_at',
      headerName: 'Timestamp',
      sortable: true,
      filter: 'agDateColumnFilter',
      filterParams: {
        filterOptions: ['before', 'after', 'inRange'],
        maxNumConditions: 1,
        buttons: ['apply', 'reset'],
        closeOnApply: true,
      },
      cellRenderer: (data: any) => {
        return data.value ? new Date(data.value).toLocaleDateString() + ' ' + new Date(data.value).toLocaleTimeString() : '';
      },
    },
    {
      field: 'type',
      headerName: 'Type',
      sortable: true,
      filter: true,
      filterParams: {
        filterOptions: [
          'empty',
          {
            displayKey: 'alert',
            displayName: 'Warnings',
            predicate: () => true,
            numberOfInputs: 0,
          },
          {
            displayKey: 'error',
            displayName: 'Errors',
            predicate: () => true,
            numberOfInputs: 0,
          },
          {
            displayKey: 'info',
            displayName: 'info',
            predicate: () => true,
            numberOfInputs: 0,
          },
        ],
        maxNumConditions: 1,
        buttons: ['apply', 'reset'],
        closeOnApply: true,
      } as ITextFilterParams,
      cellRenderer: (data: any) => {
        if (data.value === 'status') {
          return `<svg  width="24" height="24" fill="none" stroke="currentColor" class="size-6" class="bi bi-information-circle-fill flex-shrink-0 mt-2">
            <path stroke-linecap="round" stroke-linejoin="round" d="m11.25 11.25.041-.02a.75.75 0 0 1 1.063.852l-.708 2.836a.75.75 0 0 0 1.063.853l.041-.021M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9-3.75h.008v.008H12V8.25Z" />
          </svg><span style="margin-left: 2px">Info</span>
          `;
        }

        return `<svg width="24" height="24" 
             fill="${data.value === 'error' ? 'red' : data.value === 'alert' ? 'orange' : 'gray'}"
             class="bi bi-exclamation-triangle-fill flex-shrink-0 mt-2">
            <path d="M8.982 1.566a1.13 1.13 0 0 0-1.96 
                     0L.165 13.233c-.457.778.091 1.767.98 
                     1.767h13.713c.889 0 1.438-.99.98-1.767L8.982
                     1.566zM8 5c.535 0 .954.462.9.995l-.35 
                     3.507a.552.552 0 0 1-1.1 0L7.1 5.995A.905.905 
                     0 0 1 8 5zm.002 6a1 1 0 1 1 0 2 1 1 0 0 1 0-2z" />
        </svg><span>${data.value === 'error' ? 'Error' : data.value === 'alert' ? 'Warning' : 'Info'}</span>`;
      },
    },
    {
      field: 'message_type',
      headerName: 'Message Type',
      sortable: true,
      filter: true,
      filterParams: {
        filterOptions: ['contains'],
        maxNumConditions: 1,
        buttons: ['apply', 'reset'],
        closeOnApply: true,
      },
    },
    {
      field: 'name',
      headerName: 'Name',
      sortable: true,
      filter: true,
      filterParams: {
        filterOptions: ['contains'],
        maxNumConditions: 1,
        buttons: ['apply', 'reset'],
        closeOnApply: true,
      },
    },
  ];

  defaultColDef: ColDef = {
    flex: 1,
    // minWidth: 100,
    resizable: true,
    wrapText: true,
  };

  gridApi: any;

  // Update grid configuration
  gridOptions: GridOptions = {
    rowModelType: 'infinite',
    cacheBlockSize: 15,
    maxBlocksInCache: 5,
    rowBuffer: 0,
    infiniteInitialRowCount: -1,
    getRowId: params => params.data.uuid,
    autoSizeStrategy: {
      type: 'fitGridWidth',
      defaultMinWidth: 80,
    },
  };

  getRowId = (params:any) => {
    return JSON.parse(params?.data?.message ?? {}).test_id ?? params.data.id
  };

  onRowClicked(event: any): void {
    this.gridApi.deselectAll();
    this.selectedAlert = event.data;
    if (this.gridApi) {
      this.gridApi.forEachNode((node: any) => {
        if (node.data?._id === this.selectedAlert?._id) {
          node.setSelected(true);
        }
      });
      this.gridApi.refreshCells({ force: true });
    }
  }

  constructor(private publicApiService: PublicApiService) {}

  ngOnInit(): void {}

  ngOnDestroy(): void {}

  onGridReady(params: GridReadyEvent): void {
    this.gridApi = params.api;
    this.gridApi.setGridOption('datasource', this.dataSource);
    this.gridApi.sizeColumnsToFit();
  }
  onModelUpdated() {
    this.loading = false;
    if (this.gridApi) {
      this.gridApi.refreshCells({ force: true });
    }
  }
}
